// Generic CRUD building blocks shared across HR sub-sections.
import React, { useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { Loading, Empty, WorkflowBadge } from "./_shared";
import RecordInspector from "@/components/app/RecordInspector";

export function CreateDialog({ endpoint, form, onCreated, defaults = {}, testId, open, onOpenChange }) {
  const initial = Object.fromEntries(form.map((f) => [f.k, defaults[f.k] ?? ""]));
  const [vals, setVals] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...defaults };
      for (const f of form) {
        let v = vals[f.k];
        if (v === "") continue;
        if (f.type === "number") v = Number(v);
        payload[f.k] = v;
      }
      await api.post(endpoint, payload);
      toast.success("Created");
      onOpenChange(false); setVals(initial); onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><Button className="btn-primary-blue" data-testid={`${testId}-create-btn`}><Plus size={14} /> New</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New record</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {form.map((f) => (
            <div key={f.k} className={f.textarea ? "col-span-2" : "col-span-2 md:col-span-1"}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "select" ? (
                <Select value={vals[f.k]} onValueChange={(v) => setVals({ ...vals, [f.k]: v })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(typeof f.optionsFn === "function" ? f.optionsFn(vals) : (f.options || []))
                      .map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.textarea ? (
                <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={3}
                          value={vals[f.k]} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} required={f.required} />
              ) : (
                <Input type={f.type || "text"} value={vals[f.k]} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} required={f.required} />
              )}
            </div>
          ))}
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} className="btn-primary-blue">{busy ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Generic schema-agnostic detail dialog for SubList rows.
export function RecordDetailDialog({ open, onClose, record, fields, lookups, auditEntity }) {
  if (!record) return null;
  const allKeys = Array.from(new Set([
    ...(fields || []).map((f) => f[0]),
    ...Object.keys(record).filter(
      (k) => !["_id", "id", "created_at", "updated_at"].includes(k)
              && typeof record[k] !== "object"
    ),
  ]));
  const labelFor = (k) => {
    const f = (fields || []).find((x) => x[0] === k);
    return (f && f[1]) || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const fmtVal = (k, v) => {
    if (v == null || v === "") return "—";
    if (lookups && lookups[k]) return lookups[k](v) || v;
    const f = (fields || []).find((x) => x[0] === k);
    if (f && f[2] === "date") return formatDate(v);
    if (f && f[2] === "badge") return <WorkflowBadge status={v} />;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="record-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Record detail</DialogTitle>
          <DialogDescription>
            Full record · created {record.created_at ? formatDate(record.created_at) : "—"}
            {record.updated_at && record.updated_at !== record.created_at
              ? ` · updated ${formatDate(record.updated_at)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {auditEntity && (
          <div className="flex items-center justify-end mb-2">
            <AuditTrailDrawer entity={auditEntity} entityId={record.id} label="Internal Audit" />
          </div>
        )}

        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {allKeys.map((k) => (
            <div key={k} className="border-b border-slate-100 pb-1.5">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{labelFor(k)}</div>
              <div className="mt-0.5 text-slate-900">{fmtVal(k, record[k])}</div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="record-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Generic list + create dialog used by Contracts/Warnings/Defenses/Leaves.
export function SubList({ endpoint, dataKey, columns, form, testId, emptyText, createPayloadDefaults, lookups, onRowClick, detailFields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setItems(data[dataKey] || data || []); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const renderCell = (row, col) => {
    const [k, , fmt] = col;
    const v = row[k];
    if (v == null || v === "") return "—";
    if (lookups && lookups[k]) return lookups[k](v) || v;
    if (fmt === "date") return formatDate(v);
    if (fmt === "badge") return <WorkflowBadge status={v} />;
    return String(v);
  };

  const handleRowClick = (row) => {
    if (onRowClick) onRowClick(row);
    else setDetail(row);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold">{items.length} record(s)</h3>
        <CreateDialog endpoint={endpoint} form={form} onCreated={load} defaults={createPayloadDefaults} testId={testId} open={open} onOpenChange={setOpen} />
      </div>
      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : items.length === 0 ? <Empty text={emptyText} /> : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>{columns.map((c) => <th key={c[0]} className="px-5 py-3">{c[1]}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r, i) => (
                  <tr
                    key={r.id || i}
                    className="text-sm cursor-pointer hover:bg-slate-50 transition"
                    onClick={() => handleRowClick(r)}
                    data-testid={`row-${endpoint.replaceAll("/", "_")}-${r.id || i}`}
                  >
                    {columns.map((c) => (
                      <td key={c[0]} className={`px-5 py-3 ${c[1] === columns[0][1] ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                        {renderCell(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {items.map((r, i) => (
                <li
                  key={r.id || i}
                  className="p-4 text-sm cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition"
                  onClick={() => handleRowClick(r)}
                  data-testid={`mrow-${endpoint.replaceAll("/", "_")}-${r.id || i}`}
                >
                  <div className="font-semibold text-slate-900">{renderCell(r, columns[0])}</div>
                  {columns.slice(1).map((c) => (
                    <div key={c[0]} className="mt-1 text-xs text-slate-600"><span className="text-slate-400">{c[1]}: </span>{renderCell(r, c)}</div>
                  ))}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {!onRowClick && (
        <RecordInspector
          open={!!detail}
          onOpenChange={(o) => !o && setDetail(null)}
          record={detail}
          title={detail ? (
            // Pretty title: use the first column's value (often Name/Position)
            (() => {
              const firstCol = columns[0]?.[0];
              const v = firstCol ? detail[firstCol] : null;
              if (v == null || v === "") return "Record details";
              if (lookups && lookups[firstCol]) return lookups[firstCol](v) || String(v);
              return String(v);
            })()
          ) : "Record details"}
          subtitle={detail?.staff_name || detail?.staff_email || detail?.department || ""}
          testidPrefix="hr-record-inspector"
        />
      )}
    </div>
  );
}

// Shared staff create/edit form (used by Staff Directory).
export function StaffForm({ initial, onSave, children }) {
  const BLANK = {
    full_name: "", employee_code: "", email: "", phone: "", staff_type: "permanent",
    designation: "", department: "", project_id: "", join_date: "",
    salary: 0, status: "active", notes: "",
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || BLANK); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, salary: Number(form.salary) };
      await onSave(payload); setOpen(false);
    } catch { /* caller toasts */ } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{initial ? "Edit staff" : "New staff member"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div><Label>Full name *</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><Label>Employee code</Label><Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Staff type</Label>
            <Select value={form.staff_type} onValueChange={(v) => setForm({ ...form, staff_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="permanent">Permanent</SelectItem><SelectItem value="project_based">Project-Based</SelectItem><SelectItem value="consultant">Consultant</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
          <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
          <div><Label>Project ref</Label><Input value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} /></div>
          <div><Label>Join date</Label><Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} /></div>
          <div><Label>Salary (USD)</Label><Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} className="btn-primary-blue">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
